import {
  AvatarGroup,
  Box,
  Button,
  Card,
  Divider,
  List,
  ListItem,
  SxProps,
  Theme,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AssistantPhotoRoundedIcon from "@mui/icons-material/AssistantPhotoRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import { Wrapper } from "./styles";

import { Avatar } from "../../../../../components";
import { BorderLinearProgress } from "../../../../WorkflowManagement/components/WorkflowItem/border_linear_progress";
import variableStyles from "../../../../../theme/variable-styles";
import { IStageHistory, ITask } from "../../../../../models/Task";
import moment from "moment";
import dayjs from "dayjs";
import { EStageType } from "../../../../../models/common/models.enum";
import EditTrackerModal from "../../../EditTaskTrackerModal/EditTrackerModal";
import { fTime } from "../../../../../utils/formatTime";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { CountryLanguage } from "../../../../../i18n/i18n";
import { jwtDecode } from "jwt-decode";
import { ITokenPayload } from "../../../../../models/user";

type JobInformationProps = {
  rows: {
    icon: React.ReactNode;
    title: string;
    content?: string | undefined;
    createAt?: string | undefined;
  }[];
};

interface Props {
  canUpdateTrackers: boolean;
  task: ITask;
  stagesHistorys: IStageHistory[];
  onUpdateTrackersSuccess: () => void;
}
interface ProcessProps {
  task: ITask;
  stagesHistorys: IStageHistory[];
}

const calculatePercentage = ({
  expectedTime,
  completionTime
}: {
  expectedTime: string | null;
  completionTime: string | null;
}): { percentage: number; isOver: boolean } => {
  if (!completionTime) return { percentage: 0, isOver: false };

  if (expectedTime) {
    if (fTime(expectedTime)!.seconds === 0)
      return { percentage: 0, isOver: false };
    const percentage =
      fTime(completionTime)!.seconds / fTime(expectedTime)!.seconds;

    if (percentage > 1 && percentage % 1 === 0) {
      return { percentage: 1, isOver: true };
    } else if (percentage > 1 && percentage % 1 < 1) {
      return { percentage: percentage % 1, isOver: true };
    } else {
      return { percentage: percentage, isOver: false };
    }
  }

  return { percentage: 0, isOver: false };
};

const CurrentStage = ({
  index,
  task,
  stages
}: {
  index: number;
  task: ITask;
  stages: IStageHistory[];
}) => {
  const { t } = useTranslation();
  const [token, setToken] = useState<ITokenPayload>();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode<ITokenPayload>(token);

      setToken(decoded);
    }
  }, []);

  let allStage = stages.length;
  const currentStage = stages[index];
  return (
    <>
      <Card
        sx={{
          padding: "8px 16px 8px 16px",
          backgroundColor:
            currentStage.type === EStageType.Cancel
              ? "#b72727"
              : currentStage.type === EStageType.Complete
                ? "#53b727"
                : variableStyles.MainColor,
          color: "white",
          borderRadius: "3px 3px 0 0"
        }}
      >
        {currentStage.type !== EStageType.Step && (
          <>
            <Box display={"flex"} flexDirection={"row"} gap={1} padding={0.7}>
              <CheckCircleRoundedIcon />
              <Box>
                <Typography variant="subtitle1" fontSize={15}>
                  <Box
                    component="span"
                    fontWeight="fontWeightMedium"
                    color={variableStyles.NaturalColor300}
                  >
                    {t("Management.Stage")}:{" "}
                  </Box>
                  {token &&
                    (token.language === CountryLanguage.EN
                      ? (() => {
                          switch (currentStage.name.toLocaleLowerCase()) {
                            case "hoàn thành":
                              return "Completed";
                            case "hủy":
                              return "Canceled";
                            default:
                              return currentStage.name;
                          }
                        })()
                      : currentStage.name)}
                </Typography>
                {/* <Typography
                  variant="subtitle1"
                  fontSize={13}
                  paddingTop={0.5}
                  color={variableStyles.NaturalColor300}
                >
                  {currentStage.type === EStageType.Complete
                    ? "Đã hoàn thành"
                    : currentStage.type === EStageType.Cancel
                      ? "Đã bị hủy"
                      : "Đã thất bại"}
                </Typography> */}
              </Box>
            </Box>
          </>
        )}
        {currentStage.type === EStageType.Step && (
          <>
            <Typography
              variant="overline"
              fontSize={12}
              color={variableStyles.NaturalColor200}
            >
              {t("Management.CurrentStage")}
            </Typography>
            <Typography variant="subtitle1" fontSize={15} paddingTop={1}>
              [{index + 1}/{allStage}] {task.info.currentStage}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                margin: "8px 0"
              }}
            >
              <Typography variant="caption" fontSize={11} fontWeight={500}>
                {t("Management.Deadline")}:{" "}
                {task.taskCard.deadLine
                  ? dayjs(task.taskCard.deadLine).format("HH:mm DD/MM/YYYY")
                  : t("Management.NoDeadline")}
              </Typography>
              {/* <Typography variant="caption" fontSize={11} fontWeight={500}>
            Đã bắt đầu 9 ngày trước
          </Typography> */}
            </Box>
            <BorderLinearProgress
              variant="determinate"
              value={0}
              height={8}
              radius={2}
              barcolor="#308fe8"
              background="#2674c2"
            />
            {stages[index].histories && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  margin: "8px 0"
                }}
              >
                <Typography variant="caption" fontSize={11} fontWeight={500}>
                  {t("Management.Expectation")}:{" "}
                  {(() => {
                    const time = fTime(stages[index].histories!.expectedTime);
                    if (!time) return "0m";
                    return (
                      `${time.hours ? time.hours + "h" : ""}${time.minutes ? time.minutes + "m" : ""}`.trim() ||
                      "0m"
                    );
                  })()}
                </Typography>
                <Typography variant="caption" fontSize={11} fontWeight={500}>
                  {t("Management.TimeSpent")}:{" "}
                  {(() => {
                    const time = fTime(stages[index].histories!.completionTime);
                    if (!time) return "0m";
                    return (
                      `${time.hours ? time.hours + "h" : ""}${time.minutes ? time.minutes + "m" : ""}`.trim() ||
                      "0m"
                    );
                  })()}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Card>

      {index < stages.length - 1 && (
        <Card
          sx={{
            borderRadius: "0 0 3px 3px",
            backgroundColor: variableStyles.MainColor,
            display: "flex",
            padding: "6px 12px",
            alignItems: "center"
          }}
        >
          <KeyboardDoubleArrowRightRoundedIcon
            sx={{ color: "white", height: 14, width: 14 }}
          />
          <Typography
            variant="overline"
            fontSize={12}
            color={variableStyles.NaturalColor200}
          >
            {t("Management.NextStage")}:
          </Typography>
          <Typography
            variant="caption"
            fontSize={13}
            color="white"
            fontWeight={700}
            paddingX={0.5}
            lineHeight={1}
          >
            {stages[index + 1].name}
          </Typography>
          <Typography
            variant="subtitle2"
            fontSize={12}
            fontWeight={600}
            lineHeight={1.5}
            color={variableStyles.NaturalColor200}
          >
            {" (0m)"}
          </Typography>
        </Card>
      )}
    </>
  );
};

const JobInformation = ({ rows }: JobInformationProps) => {
  const { t } = useTranslation();

  return (
    <Card sx={{ margin: "10px 0", padding: "12px", borderRadius: "3px" }}>
      <Typography
        color={"#858585"}
        fontWeight={600}
        sx={{
          lineHeight: 1.5,
          fontSize: 12,
          letterSpacing: 1.1,
          textTransform: "uppercase"
        }}
      >
        {t("Management.TaskInformation")}
      </Typography>
      <List>
        {rows.map(row => (
          <ListItem key={row.title} sx={{ padding: "2px 0" }}>
            {row.icon}
            <Typography variant="body2" fontSize={13} margin="0 4px">
              {row.title}
            </Typography>
            {row.content && (
              <Typography
                variant="body2"
                fontSize={13}
                fontWeight={600}
                marginRight="4px"
              >
                {row.content}
              </Typography>
            )}
            {row.createAt && (
              <Typography variant="body2" fontSize={13} marginRight="4px">
                {row.createAt}
              </Typography>
            )}
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

/**
 * Main Progress component that displays task progress and stage history
 * @param {ProcessProps} props - Component props
 * @returns Progress information card with total time and stage details
 */

/**
 * Calculates total time for expected or actual duration
 * @param {"expected" | "actual"} timeType - Type of time to calculate
 * @returns {string} Formatted time string in "HH:mm" format
 */

const Progress = ({ task, stagesHistorys }: ProcessProps) => {
  const hourUnit = "h";
  const minuteUnit = "m";

  const currentStage = stagesHistorys.find(
    e => e.id === task.taskCard.workflowId
  );

  const currentStageIndex = stagesHistorys.findIndex(
    e => e.id === task.taskCard.workflowId
  );

  const calculateTotalTime = useCallback(
    (timeType: "expected" | "actual"): string => {
      const totalMinutes = task.stages.reduce((total, stage) => {
        const time =
          timeType === "expected"
            ? stage.histories?.expectedTime
            : stage.histories?.completionTime;
        if (time) {
          const [hours, minutes] = time.includes(":")
            ? time.split(":").map(Number)
            : [Number(time), 0];
          return total + hours * 60 + minutes;
        }
        return total;
      }, 0);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}:${minutes < 10 ? minutes.toString() : minutes.toString().padStart(2, "0")}`;
    },
    [task.stages]
  );

  const getTotalExpectedTime = useMemo(
    () => calculateTotalTime("expected"),
    [calculateTotalTime]
  );
  const getTotalActualTime = useMemo(
    () => calculateTotalTime("actual"),
    [calculateTotalTime]
  );

  const { percentage, isOver } = calculatePercentage({
    expectedTime: getTotalExpectedTime,
    completionTime: getTotalActualTime
  });

  const renderTime = (time: string | null) => {
    const defaultComponent = (
      <>
        <Box
          component="span"
          fontWeight="fontWeightMedium"
          color={variableStyles.colorBlack}
        >
          {"0"}
        </Box>
        m
      </>
    );
    if (!time) {
      return defaultComponent;
    }
    const timeFormated = fTime(time);
    if (!timeFormated) return <></>;
    if (!timeFormated.hours && !timeFormated.minutes) {
      return defaultComponent;
    }
    return (
      <>
        {timeFormated.hours && (
          <>
            <Box
              component="span"
              fontWeight="fontWeightMedium"
              color={variableStyles.colorBlack}
            >
              {timeFormated.hours}
            </Box>
            {hourUnit}
          </>
        )}
        {timeFormated.minutes && (
          <>
            {" "}
            <Box
              component="span"
              fontWeight="fontWeightMedium"
              color={variableStyles.colorBlack}
            >
              {timeFormated.minutes}
            </Box>
            {minuteUnit}
          </>
        )}
      </>
    );
  };

  const StageIndex = ({
    stage,
    isComplete: isCompleted,
    isCancel,
    index
  }: {
    index: number;
    stage: IStageHistory;
    isComplete: boolean;
    isCancel: boolean;
  }) => {
    const isActive = stage.histories;

    const colors = (): { bg: string; border: string } => {
      if (isActive && isCompleted) {
        return {
          bg: "#6ad63a",
          border: "#53b727"
        };
      }
      if (isActive && isCancel) {
        return {
          bg: "#b72727",
          border: "#891818"
        };
      }
      if (isActive) {
        return {
          bg: variableStyles.MainColor,
          border: variableStyles.MainColor
        };
      }
      return {
        bg: "#c2c2c2",
        border: "white"
      };
    };

    return (
      <Box
        width={20}
        height={20}
        borderRadius={"50%"}
        bgcolor={colors().border}
        border={"1px solid #c2c2c2"}
        color={"white"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Box
          width={14}
          height={14}
          borderRadius={"50%"}
          bgcolor={colors().bg}
          textAlign={"center"}
          fontSize={10}
          lineHeight={1.5}
        >
          {index}
        </Box>
      </Box>
    );
  };
  /**
   * Individual progress item component for each stage
   * @param {Object} props - Component props
   * @param {number} props.index - Stage index
   * @param {IStageHistory} props.stage - Stage history data
   * @param {string} props.noHistoryValue - Text to display when no history exists
   * @param {boolean} props.isComplete - Whether the stage is completed
   * @param {boolean} props.isCancel - Whether the stage is cancelled
   * @returns Progress item with stage details, time information, and user data
   */
  const ProgressItem = ({
    index,
    stage,
    noHistoryValue,
    isComplete,
    isCancel
  }: {
    index: number;
    stage: IStageHistory;
    noHistoryValue: string;
    isComplete: boolean;
    isCancel: boolean;
  }) => {
    const { percentage, isOver } = calculatePercentage({
      expectedTime: stage.histories?.expectedTime ?? null,
      completionTime: stage.histories?.completionTime ?? null
    });

    const isShowTime = stage.histories && stage.type === EStageType.Step;

    return (
      <>
        <Divider sx={{ margin: "10px 0", borderColor: "#eeeeee" }} />
        <Box display={"flex"} flexDirection={"row"}>
          <StageIndex
            index={index}
            stage={stage}
            isComplete={isComplete}
            isCancel={isCancel}
          />
          <Box
            display={"flex"}
            flexDirection={"column"}
            flexGrow={1}
            marginLeft={1.5}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
            >
              <Typography
                variant="caption"
                fontWeight={500}
                fontSize={12}
                lineHeight={1}
              >
                {stage.name}
              </Typography>
              {stage.histories?.completionDateTime && (
                <Typography
                  variant="caption"
                  fontSize={12}
                  fontWeight={500}
                  color={"#a9a9a9"}
                >
                  {dayjs(stage.histories.completionDateTime).format(
                    "HH:mm DD/MM/YYYY"
                  )}
                </Typography>
              )}
            </Box>
            {stage.histories && (
              <>
                <BorderLinearProgress
                  variant="determinate"
                  value={percentage * 100}
                  height={8}
                  radius={2}
                  barcolor={isOver ? "#be495d" : "#53b727"}
                  background="#eeeeee"
                  sx={{ margin: "4px 0" }}
                />
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                >
                  {isShowTime && (
                    <Typography
                      variant="caption"
                      fontSize={12}
                      fontWeight={500}
                      color={"#a9a9a9"}
                    >
                      {t("Management.Expectation")}:{" "}
                      {renderTime(stage.histories.expectedTime)}
                    </Typography>
                  )}
                  {isShowTime && (
                    <Typography
                      variant="caption"
                      fontSize={12}
                      fontWeight={500}
                      color={"#a9a9a9"}
                    >
                      {t("Management.Actual")}:{" "}
                      {renderTime(stage.histories.completionTime)}
                    </Typography>
                  )}
                </Box>
                {stage.histories.user && (
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    alignItems={"center"}
                    margin={"4px 0"}
                  >
                    <Avatar
                      src={stage.histories.user.avatar}
                      alt="Avatar-Image"
                      width={20}
                      height={20}
                    />

                    <Typography fontSize={12} paddingLeft={1}>
                      {stage.histories.user.fullName}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {!stage.histories && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                paddingTop={0.6}
              >
                <Typography
                  variant="caption"
                  fontSize={12}
                  fontWeight={500}
                  color={"#a9a9a9"}
                >
                  {t("Management.Duration")}:
                  <Box
                    component="span"
                    fontWeight="fontWeightMedium"
                    color={variableStyles.colorBlack}
                  >
                    {" 0"}
                  </Box>
                  {minuteUnit}
                </Typography>
                <Typography
                  variant="caption"
                  fontSize={12}
                  fontWeight={500}
                  color={"#a9a9a9"}
                >
                  {noHistoryValue}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Card sx={{ marginTop: "10px", padding: "16px", borderRadius: "3px" }}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography
          color={"#858585"}
          fontWeight={600}
          sx={{
            lineHeight: 1.5,
            fontSize: 12,
            letterSpacing: 1.1,
            textTransform: "uppercase"
          }}
        >
          {t("Common.TotalTime")}
        </Typography>
        <Typography
          fontSize={12}
          color={"#858585"}
          sx={{ display: "inline", inlineSize: "max-content" }}
        >
          {t("Common.TotalTime") + " "} {renderTime(getTotalActualTime)}
          {" " + t("Common.Of") + " "} {renderTime(getTotalExpectedTime)}
        </Typography>
      </Box>

      <BorderLinearProgress
        variant="determinate"
        value={percentage}
        height={8}
        radius={2}
        barcolor={isOver ? "#be495d" : "#53b727"}
        background="#eeeeee"
        sx={{ margin: "8px 0" }}
      />

      <Typography
        fontWeight={600}
        sx={{
          lineHeight: 1.5,
          fontSize: 12,
          letterSpacing: 1.1,
          textTransform: "uppercase"
        }}
      >
        {t("Management.StageProgress")}
      </Typography>

      {stagesHistorys.map((e, i) => (
        <ProgressItem
          index={i}
          stage={e}
          key={e.id}
          noHistoryValue={
            currentStageIndex < i
              ? t("Management.WaitingStage")
              : currentStageIndex > i
                ? t("Management.SkippedStage")
                : ""
          }
          isComplete={currentStage?.type === EStageType.Complete}
          isCancel={currentStage?.type === EStageType.Cancel}
        />
      ))}
      <Divider sx={{ margin: "8px 0", borderColor: "#eeeeee" }} />
    </Card>
  );
};

const JobDetailProgress = ({
  canUpdateTrackers,
  task,
  stagesHistorys,
  onUpdateTrackersSuccess
}: Props) => {
  const { t } = useTranslation();

  let currentStageIndex = stagesHistorys.findIndex(
    e => e.id === task.taskCard.workflowId
  );

  const [openUpdateTrackers, setOpenUpdateTrackers] = useState(false);

  const getDate = useCallback(
    (date: string | null, type?: "short") => {
      if (!date) return "";
      return moment(date).format(
        type === "short" ? "DD/MM/YYYY" : "HH:mm DD/MM/YYYY"
      );
    },

    []
  );

  const sx: SxProps<Theme> = {
    fontSize: 16,
    color: "grey",
    marginRight: "4px"
  };

  const rows = [
    // {
    //   icon: <TagRoundedIcon sx={sx} />,
    //   title: "Mã nhiệm vụ:",
    //   content: "1725289"
    // },
    {
      icon: <AccountCircleRoundedIcon sx={sx} />,
      title: t("Common.CreatedBy"),
      content: task.info.creator ?? "",
      createAt: t("Common.At") + " " + getDate(task.info.createdDate)
    },
    {
      icon: <AccessTimeRoundedIcon sx={sx} />,
      title: t("Common.LastUpdated"),
      createAt:
        t("Common.At").toLocaleLowerCase() +
        " " +
        getDate(task.info.modifiedDate ?? task.info.createdDate, "short")
    },
    {
      icon: <AssistantPhotoRoundedIcon sx={sx} />,
      title: t("Management.CurrentStage") + ":",
      content: stagesHistorys[currentStageIndex].name
    },
    {
      icon: <AccessTimeRoundedIcon sx={sx} />,
      title: t("Management.MovedToStageAt") + ":",
      createAt: getDate(task.info.stageTransitionAt)
    },
    {
      icon: <BookmarkRoundedIcon sx={sx} />,
      title: t("Management.StageAdministrator") + ":",
      content: task.info.managerStage ?? t("Common.NotSet")
    }
  ];

  return (
    <Wrapper>
      <CurrentStage
        stages={stagesHistorys}
        task={task}
        index={currentStageIndex}
      />
      <JobInformation rows={rows} />
      <Card
        sx={{
          margin: "10px 0",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          justifyItems: "flex-start",
          borderRadius: "3px"
        }}
      >
        <Box display={"flex"} flexDirection={"row"}>
          <Typography
            flexGrow={1}
            color={"#858585"}
            fontWeight={600}
            sx={{
              lineHeight: 1.5,
              fontSize: 12,
              letterSpacing: 1.1,
              textTransform: "uppercase"
            }}
          >
            {t("Management.TaskFollower")}
          </Typography>

          {canUpdateTrackers && (
            <Button
              sx={{ color: "#2E9CC9", marginLeft: 1 }}
              onClick={() => setOpenUpdateTrackers(true)}
            >
              <Typography flexGrow={1} fontSize={12}>
                {t("Management.EditFollowers")}
              </Typography>
            </Button>
          )}
        </Box>

        <AvatarGroup
          sx={{
            alignSelf: "start",
            marginTop: 1,
            ".MuiAvatar-root": { width: 26, height: 26 }
          }}
          max={4}
          renderSurplus={surplus => (
            <Typography fontSize={16}>+{surplus}</Typography>
          )}
          total={task.taskCard.taskTrackers?.length || 0}
        >
          {task.taskCard.taskTrackers
            ?.slice(0, 4)
            .map(tracker => (
              <Avatar
                key={`taskTracker ${tracker.id}`}
                height={26}
                width={26}
                alt={tracker.fullName ?? "User avatar"}
                src={tracker.avatar}
              />
            ))}
        </AvatarGroup>
      </Card>
      <Card
        sx={{
          margin: "10px 0",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          justifyItems: "flex-start",
          borderRadius: "3px"
        }}
      >
        <Box display={"flex"} flexDirection={"row"}>
          <Typography
            flexGrow={1}
            color={"#858585"}
            fontWeight={600}
            sx={{
              lineHeight: 1.5,
              fontSize: 12,
              letterSpacing: 1.1,
              textTransform: "uppercase"
            }}
          >
            {t("Management.StageFollower")}
          </Typography>
        </Box>

        <AvatarGroup
          sx={{
            alignSelf: "start",
            marginTop: 1,
            ".MuiAvatar-root": { width: 26, height: 26 }
          }}
          max={4}
          renderSurplus={surplus => (
            <Typography fontSize={16}>+{surplus}</Typography>
          )}
          total={task.taskCard.stageTrackers?.length || 0}
        >
          {task.taskCard.stageTrackers
            ?.slice(0, 4)
            .map(tracker => (
              <Avatar
                key={`stageTracker ${tracker.id}`}
                height={26}
                width={26}
                alt={tracker.fullName ?? "User avatar"}
                src={tracker.avatar}
              />
            ))}
        </AvatarGroup>
      </Card>
      <Card
        sx={{
          margin: "10px 0",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          justifyItems: "flex-start",
          borderRadius: "3px"
        }}
      >
        <Box display={"flex"} flexDirection={"row"}>
          <Typography
            flexGrow={1}
            color={"#858585"}
            fontWeight={600}
            sx={{
              lineHeight: 1.5,
              fontSize: 12,
              letterSpacing: 1.1,
              textTransform: "uppercase"
            }}
          >
            {t("Management.WorkflowFollower")}
          </Typography>
        </Box>

        <AvatarGroup
          sx={{
            alignSelf: "start",
            marginTop: 1,
            ".MuiAvatar-root": { width: 26, height: 26 }
          }}
          max={4}
          renderSurplus={surplus => (
            <Typography fontSize={16}>+{surplus}</Typography>
          )}
          total={task.taskCard.workflowTrackers?.length || 0}
        >
          {task.taskCard.workflowTrackers
            ?.slice(0, 4)
            .map(tracker => (
              <Avatar
                key={`workflowTracker ${tracker.id}`}
                height={26}
                width={26}
                alt={tracker.fullName ?? "User avatar"}
                src={tracker.avatar}
              />
            ))}
        </AvatarGroup>
      </Card>
      <Progress task={task} stagesHistorys={stagesHistorys} />
      <EditTrackerModal
        open={openUpdateTrackers}
        onClose={() => setOpenUpdateTrackers(false)}
        onUpdateSuccess={onUpdateTrackersSuccess}
        task={task.taskCard}
      />
    </Wrapper>
  );
};

export default JobDetailProgress;
